/**
 * MiniProjectTaskDetailTray (DEV-1288) — the EXECUTION/VIEW surface for a
 * task, distinct from MiniProjectTaskEditTray (authoring). Opens when anyone
 * clicks a task; shows what the task is and how to do it, and lets the
 * ASSIGNEE (not just the Lead) act on it:
 *   - read: description, instruction steps, how-to link, due date, evidence
 *   - act (assignee or Lead): add/remove evidence, write a completion note,
 *     mark done, reopen
 *   - Lead only: an "Edit task" button that hands off to the authoring tray
 *
 * Backend already permits assignee self-service (MiniProjectAuth
 * .CanUpdateOwnTaskStatus gates the /transition + /evidence endpoints), so
 * this is a pure UI surface over existing, tested calls.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  transitionMiniProjectTaskStatus,
  addMiniProjectTaskEvidence,
  removeMiniProjectTaskEvidence,
  toggleMiniProjectTaskStep,
  type MiniProjectTask,
} from "../../api/miniProjectTasks";
import {
  parseInstructionSteps,
  isSafeHttpUrl,
  requiredStepsSatisfied,
} from "../../Data/MiniProjectConfig/instructionSteps";
import { uploadFile, fileUrl, fileNameFromKey } from "../../api/fileStorage";
import { Tray } from "../Tray/Tray";
import styles from "./MiniProjectTaskDetailTray.module.css";

interface Props {
  open: boolean;
  task: MiniProjectTask | null;
  miniProjectGuid: string;
  /** Assignee or Lead — may add evidence / note and change status. */
  canAct: boolean;
  /** Lead — may open the structural authoring tray. */
  isLead: boolean;
  onClose: () => void;
  onMutated: () => void;
  /** Hand off to the authoring (edit) tray. */
  onEdit: () => void;
}

const STATUS_LABEL_KEY: Record<string, string> = {
  not_started: "miniProject.cards.task.status.notStarted",
  in_progress: "miniProject.cards.task.status.inProgress",
  done: "miniProject.cards.task.status.done",
};

/** Soft client-side cap; the backend re-validates. */
const MAX_BYTES = 10 * 1024 * 1024;

const MiniProjectTaskDetailTray: React.FC<Props> = ({
  open,
  task: taskProp,
  miniProjectGuid,
  canAct,
  isLead,
  onClose,
  onMutated,
  onEdit,
}) => {
  const { t } = useAppTranslation("pages");

  // Local copy of the task so in-place edits (step checks, evidence) update
  // the tray WITHOUT a parent refetch — a refetch churns the page tree and
  // closes the tray (DEV-1289). We sync from the prop when the parent hands
  // us a new task, and re-sync the list only when the tray closes.
  const [localTask, setLocalTask] = useState(taskProp);
  useEffect(() => { setLocalTask(taskProp); }, [taskProp]);
  const task = localTask ?? taskProp;

  // Closing is the ONLY moment we push changes back to the list (so step/
  // evidence edits made while open are reflected). Wired to every close path.
  const handleClose = useCallback(() => {
    onMutated();
    onClose();
  }, [onMutated, onClose]);

  const [note, setNote] = useState<string>(taskProp?.completionNote ?? "");
  const [busy, setBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDone = task?.status === "done";

  // Instruction steps — shared model with the authoring tray (DEV-1289).
  const steps = useMemo(() => parseInstructionSteps(task?.instructionSteps), [task?.instructionSteps]);
  const requiredIncomplete = !requiredStepsSatisfied(steps);

  const evidenceKeys = task?.evidenceAttachmentKeys ?? [];

  const handleToggleStep = useCallback(
    async (index: number, checked: boolean) => {
      if (!task) return;
      setBusy(true);
      setError(null);
      try {
        const updated = await toggleMiniProjectTaskStep(miniProjectGuid, task.taskGuid, index, checked);
        setLocalTask(updated); // local-only — no refetch, so the tray stays open
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [task, miniProjectGuid],
  );

  const handleFilesPicked = useCallback(
    async (files: FileList | null) => {
      if (!task || !files || files.length === 0) return;
      const picked = Array.from(files);
      const tooLarge = picked.filter((f) => f.size > MAX_BYTES);
      if (tooLarge.length > 0) {
        setError(t("taskDetail.fileTooLarge", { max: Math.round(MAX_BYTES / 1024 / 1024) }));
        return;
      }
      setError(null);
      setUploadBusy(true);
      try {
        const results = await Promise.all(
          picked.map((f) => uploadFile(f, "mp-task-evidence", task.taskGuid)),
        );
        const updated = await addMiniProjectTaskEvidence(miniProjectGuid, task.taskGuid, results.map((r) => r.key));
        setLocalTask(updated); // local-only — no refetch
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setUploadBusy(false);
      }
    },
    [task, miniProjectGuid, t],
  );

  const handleRemoveEvidence = useCallback(
    async (key: string) => {
      if (!task) return;
      setBusy(true);
      setError(null);
      try {
        const updated = await removeMiniProjectTaskEvidence(miniProjectGuid, task.taskGuid, key);
        setLocalTask(updated); // local-only — no refetch
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [task, miniProjectGuid],
  );

  const handleMarkDone = useCallback(async () => {
    if (!task) return;
    setBusy(true);
    setError(null);
    try {
      // Evidence is already persisted via upload-on-pick, so pass [].
      await transitionMiniProjectTaskStatus(
        miniProjectGuid,
        task.taskGuid,
        "done",
        note.trim() || undefined,
        [],
      );
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }, [task, miniProjectGuid, note, handleClose]);

  const handleReopen = useCallback(async () => {
    if (!task) return;
    setBusy(true);
    setError(null);
    try {
      await transitionMiniProjectTaskStatus(miniProjectGuid, task.taskGuid, "in_progress");
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }, [task, miniProjectGuid, handleClose]);

  const title = task
    ? task.nameText || (task.nameKey && t(task.nameKey)) || t("miniProject.cards.task.noName")
    : "";

  return (
    <Tray open={open} onClose={handleClose} title={title}>
      {task && (
        <>
          <Tray.Body className={styles.body}>
            <div className={styles.statusLine}>
              <span className={`${styles.statusPill} ${styles[`status_${task.status}`]}`}>
                {t(STATUS_LABEL_KEY[task.status] ?? STATUS_LABEL_KEY.not_started)}
              </span>
              {task.dueDate && (
                <span className={styles.due}>
                  {t("miniProject.sections.formLabel.dueDate")}: {task.dueDate}
                </span>
              )}
            </div>

            {task.description?.trim() && (
              <section className={styles.section}>
                <h4 className={styles.h}>{t("taskDetail.instructions")}</h4>
                <p className={styles.prose}>{task.description}</p>
              </section>
            )}

            {steps.length > 0 && (
              <section className={styles.section}>
                <h4 className={styles.h}>{t("taskDetail.steps")}</h4>
                <ul className={styles.steps}>
                  {steps.map((s, i) => (
                    <li key={s.id} className={styles.stepRow}>
                      <label className={styles.stepCheck}>
                        <input
                          type="checkbox"
                          checked={s.checked}
                          disabled={!canAct || busy}
                          onChange={(e) => handleToggleStep(i, e.target.checked)}
                        />
                        <span className={s.checked ? styles.stepLabelDone : styles.stepLabel}>
                          {s.label}
                          {s.required && <span className={styles.req} title={t("taskDetail.requiredStep")}> *</span>}
                        </span>
                      </label>
                      {s.url && isSafeHttpUrl(s.url) && (
                        <a
                          className={styles.stepLink}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={s.url}
                        >
                          {s.urlLabel || t("taskDetail.stepLinkDefault")}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {task.howToUrl && (
              <section className={styles.section}>
                <a className={styles.howTo} href={task.howToUrl} target="_blank" rel="noopener noreferrer">
                  {t("taskDetail.howToLink")}
                </a>
              </section>
            )}

            <section className={styles.section}>
              <h4 className={styles.h}>{t("taskDetail.evidence")}</h4>
              {task.requiresEvidence && evidenceKeys.length === 0 && (
                <div className={styles.evidenceReq}>{t("taskDetail.evidenceRequired")}</div>
              )}
              {evidenceKeys.length === 0 ? (
                <div className={styles.muted}>{t("taskDetail.noEvidence")}</div>
              ) : (
                <ul className={styles.evidenceList}>
                  {evidenceKeys.map((key) => (
                    <li key={key} className={styles.evidenceItem}>
                      <a href={fileUrl(key)} target="_blank" rel="noopener noreferrer" className={styles.evidenceLink}>
                        {fileNameFromKey(key)}
                      </a>
                      {canAct && (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => handleRemoveEvidence(key)}
                          disabled={busy}
                          aria-label={t("taskDetail.removeEvidence")}
                          title={t("taskDetail.removeEvidence")}
                        >
                          ✕
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {canAct && (
                <label className={styles.uploadLabel}>
                  <input
                    type="file"
                    multiple
                    className={styles.fileInput}
                    onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = ""; }}
                    disabled={uploadBusy || busy}
                  />
                  <span className={styles.uploadBtn}>
                    {uploadBusy ? t("taskDetail.uploading") : t("taskDetail.addEvidence")}
                  </span>
                </label>
              )}
            </section>

            {canAct && (
              <section className={styles.section}>
                <h4 className={styles.h}>{t("taskDetail.note")}</h4>
                <textarea
                  className={styles.textarea}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("taskDetail.notePlaceholder")}
                  rows={3}
                  disabled={busy}
                />
              </section>
            )}

            {error && <div className={styles.error}>{error}</div>}
          </Tray.Body>

          <Tray.Footer className={styles.footer}>
            {isLead && (
              <button type="button" className={styles.btn} onClick={onEdit} disabled={busy}>
                {t("taskDetail.edit")}
              </button>
            )}
            <span className={styles.spacer} />
            <button type="button" className={styles.btn} onClick={handleClose} disabled={busy}>
              {t("taskDetail.close")}
            </button>
            {canAct && (
              isDone ? (
                <button type="button" className={styles.btn} onClick={handleReopen} disabled={busy}>
                  {busy ? t("taskDetail.saving") : t("taskDetail.reopen")}
                </button>
              ) : (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleMarkDone}
                  disabled={busy || requiredIncomplete}
                  title={requiredIncomplete ? t("taskDetail.requiredBlocked") : undefined}
                >
                  {busy ? t("taskDetail.saving") : t("taskDetail.markDone")}
                </button>
              )
            )}
          </Tray.Footer>
        </>
      )}
    </Tray>
  );
};

export default MiniProjectTaskDetailTray;
